import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { TournamentsRepository } from '../services/repository';
import { showSnackbar } from './Snackbar.jsx';
import TournamentTypeFlag from './TournamentTypeFlag.jsx';
import { getPlayersWithMatchDetails } from '../utils/tournamentRanking.js';
import './TournamentSummary.css';

const TournamentSummary = () => {
    const { id } = useParams();
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [playerStats, setPlayerStats] = useState([]);
    const playerMatchesSectionRef = useRef(null);

    useEffect(() => {
        if (id) {
            loadTournament();
        }
    }, [id]);

    const loadTournament = async () => {
        try {
            setLoading(true);
            const tournamentData = await TournamentsRepository.fetchTournament(id);
            setTournament(tournamentData);
            calculatePlayerStats(tournamentData);
        } catch (error) {
            console.error('Error loading tournament:', error);
            showSnackbar('Błąd ładowania turnieju');
        } finally {
            setLoading(false);
        }
    };

    const calculatePlayerStats = (tournament) => {
        if (!tournament) return;
        
        // Use shared utility to get players with ranking and match details
        const playersWithMatches = getPlayersWithMatchDetails(tournament);
        setPlayerStats(playersWithMatches);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Nieznana data';
        const date = new Date(dateString * 1000);
        return date.toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getFinishDate = (dateString) => {
        if (!dateString) return 'Nieznana data';
        const date = new Date(dateString * 1000);
        return date.toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handlePlayerSelect = (player) => {
        const isSelectingNewPlayer = selectedPlayer?.id !== player.id;
        setSelectedPlayer(selectedPlayer?.id === player.id ? null : player);
        
        // Scroll to player matches section when selecting a new player
        if (isSelectingNewPlayer) {
            setTimeout(() => {
                if (playerMatchesSectionRef.current) {
                    playerMatchesSectionRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start',
                        inline: 'nearest'
                    });
                    
                    // Add visual highlight
                    playerMatchesSectionRef.current.classList.add('scrolled-to');
                    
                    // Remove highlight after animation
                    setTimeout(() => {
                        if (playerMatchesSectionRef.current) {
                            playerMatchesSectionRef.current.classList.remove('scrolled-to');
                        }
                    }, 2000);
                }
            }, 100); // Small delay to ensure the section is rendered
        }
    };

    if (loading) {
        return (
            <div className="tournament-summary-container">
                <div className="loading-message">Ładowanie podsumowania turnieju...</div>
            </div>
        );
    }

    if (!tournament) {
        return (
            <div className="tournament-summary-container">
                <div className="error-message">Turniej nie został znaleziony</div>
            </div>
        );
    }

    // Include all players with rank 3 or better in the top players section
    // This handles cases where there are ties for 3rd place
    const top3Players = playerStats.filter(player => player.rank <= 3);
    const otherPlayers = playerStats.filter(player => player.rank > 3);
    const selectedPlayedMatches = selectedPlayer
        ? selectedPlayer.matches.filter((m) => !m.isBench)
        : [];

    return (
        <div className="tournament-summary-container">
            <div className="summary-header">
                <div className="tournament-title">
                    <h1>🏆 <TournamentTypeFlag tournamentType={tournament.type} />{tournament.name}</h1>
                </div>
                
                <div className="tournament-dates">
                    <div className="date-info">
                        <span className="date-label">📅 Rozpoczęty:</span>
                        <span className="date-value">{formatDate(tournament.createdAt)}</span>
                    </div>
                    {tournament.finishedAt && (
                        <div className="date-info">
                            <span className="date-label">🏁 Zakończony:</span>
                            <span className="date-value">{getFinishDate(tournament.finishedAt)}</span>
                        </div>
                    )}
                </div>


            </div>

            <div className="summary-content">
                <div className="rankings-section">
                    <div className="top-players">
                        <h2>🥇 Top Gracze</h2>
                        <div className="top-players-list">
                            {top3Players.map((player) => (
                                <div 
                                    key={player.id} 
                                    className={`top-player-card rank-${player.rank} ${selectedPlayer?.id === player.id ? 'selected' : ''}`}
                                    onClick={() => handlePlayerSelect(player)}
                                >
                                    <div className="player-rank">
                                        {player.rank}
                                        {top3Players.filter(p => p.rank === player.rank).length > 1 && 
                                         <span className="tie-indicator" title="Remis">=</span>
                                        }
                                    </div>
                                    <div className="player-info">
                                        <div className="player-name-line">
                                            <div className="player-name">{player.name}</div>
                                        </div>
                                        <div className="player-record-line">
                                            <span className="win-loss">
                                                {player.wins}W - {player.ties}R - {player.losses}P
                                            </span>
                                        </div>
                                    </div>
                                    <div className="points">{player.points} pkt</div>
                                    <div className="rank-medal">
                                        {player.rank === 1 && '🥇'}
                                        {player.rank === 2 && '🥈'}
                                        {player.rank === 3 && '🥉'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {otherPlayers.length > 0 && (
                        <div className="other-players">
                            <div className="other-players-grid">
                                {otherPlayers.map((player) => (
                                    <div 
                                        key={player.id}
                                        className={`other-player-card ${selectedPlayer?.id === player.id ? 'selected' : ''}`}
                                        onClick={() => handlePlayerSelect(player)}
                                    >
                                        <div className="player-rank-badge">
                                            {player.rank}
                                            {otherPlayers.filter(p => p.rank === player.rank).length > 1 && 
                                             <span className="tie-indicator" title="Remis">=</span>
                                            }
                                        </div>
                                        <div className="player-info">
                                            <div className="player-name-line">
                                                <div className="player-name">{player.name}</div>
                                            </div>
                                            <div className="player-record-line">
                                                <span className="player-record">
                                                    {player.wins}W  - {player.ties}R - {player.losses}P
                                                </span>
                                            </div>
                                        </div>
                                        <div className="player-points">{player.points} pkt</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {selectedPlayer && (
                    <div className="player-matches-section" ref={playerMatchesSectionRef}>
                        <h3>🎾 Historia meczów: {selectedPlayer.name}</h3>
                        <div className="player-summary">
                            <div className="summary-stat">
                                <span className="stat-label">Punkty:</span>
                                <span className="stat-value">{selectedPlayer.points}</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Zwycięstwa:</span>
                                <span className="stat-value success">{selectedPlayer.wins}</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Porażki:</span>
                                <span className="stat-value danger">{selectedPlayer.losses}</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Remisy:</span>
                                <span className="stat-value warning">{selectedPlayer.ties}</span>
                            </div>
                            <div className="summary-stat">
                                <span className="stat-label">Procent wygranych:</span>
                                <span className="stat-value">
                                    {selectedPlayedMatches.length > 0
                                        ? Math.round((selectedPlayer.wins / selectedPlayedMatches.length) * 100)
                                        : 0}%
                                </span>
                            </div>
                        </div>

                        <div className="matches-list">
                            {selectedPlayer.matches.map((matchData) =>
                                matchData.isBench ? (
                                    <div
                                        key={`bench-${matchData.roundOrder}`}
                                        className="match-card bench"
                                    >
                                        <div className="match-result bench-row">
                                            <span className="result-badge bench-badge" title="Ławka">
                                                Ł
                                            </span>
                                            <div className="bench-info">
                                                <span className="bench-round">
                                                    Runda {matchData.roundDisplayNumber}
                                                </span>
                                                <span className="bench-label">Ławka (nie grał)</span>
                                                {matchData.benchPoints > 0 && (
                                                    <span className="bench-points">
                                                        +{matchData.benchPoints}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        key={matchData.match?.id ?? `m-${matchData.roundOrder}-${matchData.teamScore}`}
                                        className={`match-card ${matchData.isTie ? 'tie' : (matchData.won ? 'won' : 'lost')}`}
                                    >
                                        <div className="match-result">
                                            <span className={`result-badge ${matchData.isTie ? 'tie' : (matchData.won ? 'win' : 'loss')}`}>
                                                {matchData.isTie ? 'R' : (matchData.won ? 'W' : 'P')}
                                            </span>
                                            <span className="score">
                                                {matchData.teamScore} - {matchData.opponentScore}
                                            </span>
                                            <span className="match-round-label">
                                                Runda {matchData.roundDisplayNumber}
                                            </span>
                                        </div>
                                        <div className="match-details">
                                            <div className="teammates">
                                                <span className="label">Partner:</span>
                                                <span className="names">{matchData.teammates.join(', ') || 'Brak'}</span>
                                            </div>
                                            <div className="opponents">
                                                <span className="label">Przeciwnicy:</span>
                                                <span className="names">{matchData.opponents.join(', ')}</span>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TournamentSummary;
